import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import PartnerForm from "@/components/forms/PartnerForm";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";

const PartnerRegistration: React.FC = () => {
  const { toast } = useToast();
  const [password, setPassword] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showThankYou, setShowThankYou] = useState(false);

  // Server-side password verification
  const verifyPassword = async () => {
    setIsChecking(true);
    setError(null);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "verify-partner-password",
        { body: { password } }
      );

      if (invokeError) {
        setError("Σφάλμα επικοινωνίας. Δοκιμάστε ξανά αργότερα.");
        toast({
          title: "Σφάλμα",
          description: "Πρόβλημα κατά την επαλήθευση του κωδικού.",
          variant: "destructive",
        });
        return;
      }

      if (data?.success) {
        setAuthenticated(true);
        toast({
          title: "Επιτυχής επαλήθευση",
          description: "Μπορείτε τώρα να συμπληρώσετε τη φόρμα εγγραφής συνεργάτη.",
        });
      } else {
        setError(data?.message || "Λάθος κωδικός. Παρακαλώ δοκιμάστε ξανά.");
        toast({
          title: "Λάθος κωδικός",
          description: data?.message || "Ο κωδικός που εισάγατε είναι λανθασμένος.",
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error("Password verification error:", err);
      setError("Σφάλμα επικοινωνίας. Δοκιμάστε ξανά αργότερα.");
      toast({
        title: "Σφάλμα",
        description: "Πρόβλημα κατά την επαλήθευση του κωδικού.",
        variant: "destructive",
      });
    } finally {
      setIsChecking(false);
    }
  };

  // Success handler no longer redirects
  const handleSuccess = () => {
    toast({
      title: "Επιτυχής εγγραφή!",
      description: "Η εγγραφή σας ολοκληρώθηκε επιτυχώς. Θα ενημερωθείτε σύντομα.",
    });
    setShowThankYou(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-primary mb-2">Εγγραφή Νέου Συνεργάτη</h1>
          <p className="text-muted-foreground">
            Συμπληρώστε τη φόρμα για να εγγραφείτε ως συνεργάτης
          </p>
        </div>

        {!authenticated ? (
          <Card className="shadow-lg animate-fade-in">
            <CardHeader>
              <CardTitle className="text-xl text-primary">Επαλήθευση Κωδικού</CardTitle>
            </CardHeader>
            <CardContent>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  verifyPassword();
                }}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <Label htmlFor="password">Κωδικός Πρόσβασης</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Εισάγετε τον κωδικό που σας δόθηκε"
                    className={error ? "border-red-500" : ""}
                  />
                  {error && <p className="text-sm text-red-500">{error}</p>}
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isChecking || !password}
                >
                  {isChecking ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Επαλήθευση...
                    </>
                  ) : (
                    "Συνέχεια"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <div className="animate-fade-in">
            {/* Show Thank You badge above the form if registration is complete */}
            {showThankYou && (
              <div className="flex justify-center mb-4">
                <Badge variant="success" className="text-lg px-6 py-3">
                  Ευχαριστούμε! Η αίτησή σας υποβλήθηκε επιτυχώς και θα επικοινωνήσουμε μαζί σας σύντομα.
                </Badge>
              </div>
            )}

            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">Φόρμα Εγγραφής Συνεργάτη</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Disable the form if registration completed, but keep showing it for UX clarity */}
                <div className={showThankYou ? "opacity-40 pointer-events-none" : ""}>
                  <PartnerForm onSuccess={handleSuccess} />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerRegistration;
